import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MdImage, MdVideocam, MdRefresh, MdAutoAwesome, MdErrorOutline } from 'react-icons/md';
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import {
  updateCollectionMedia,
  regenerateImageRequest,
  regenerateVideoRequest,
  fetchUserCollectionMediaRequest,
} from '../../redux/slices/collectionsSlice';
import { APP_CONFIG } from '../../config/app.config';
import {
  monitorCollectionMedia,
  type MediaMonitorEvent,
} from '../../utils/collectionMediaMonitor';
import {
  extractImageUrls,
  canRegenerateImage,
  canRegenerateVideo,
} from '../../utils/collectionMediaHelpers';
import type { Collection, CollectionMediaData } from '../../types/collections.types';

type TabType = 'image' | 'video';
type MediaStatus = 'pending' | 'processing' | 'completed' | 'failed';

interface CollectionMediaViewerProps {
  collectionId: string;
  collection: Collection;
}

// ---------------------------------------------------------------------------
// Status badge config
// ---------------------------------------------------------------------------
const statusConfig: Record<MediaStatus, { text: string; cls: string }> = {
  pending: { text: 'Pending', cls: 'bg-blue-500/20 text-blue-400' },
  processing: { text: 'Processing', cls: 'bg-yellow-500/20 text-yellow-400' },
  completed: { text: 'Ready', cls: 'bg-green-500/20 text-green-400' },
  failed: { text: 'Failed', cls: 'bg-red-500/20 text-red-400' },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const CollectionMediaViewer: React.FC<CollectionMediaViewerProps> = ({
  collectionId,
  collection,
}) => {
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState<TabType>('image');
  const [isMonitoring, setIsMonitoring] = useState(false);

  // Refs for SSE cleanup
  const isMountedRef = useRef(true);
  const sseCleanupRef = useRef<(() => void) | null>(null);

  // Media from Redux
  const media: CollectionMediaData | undefined = useAppSelector(
    (s) => s.collections.collectionMedia[collectionId]
  );
  const mediaError = useAppSelector((s) => s.collections.mediaError);
  const isRegeneratingImg = useAppSelector(
    (s) => s.collections.regeneratingImage[collectionId] || false
  );
  const isRegeneratingVid = useAppSelector(
    (s) => s.collections.regeneratingVideo[collectionId] || false
  );

  const imgStatus = media?.image_status as MediaStatus | undefined;
  const vidStatus = media?.video_status as MediaStatus | undefined;

  // ---------------------------------------------------------------------------
  // SSE Monitoring — auto-start when media is pending/processing
  // ---------------------------------------------------------------------------
  const startMediaMonitoring = useCallback(() => {
    if (!collectionId || collectionId.startsWith('generated_') || isMonitoring) return;

    if (import.meta.env.DEV) console.log('[MediaViewer] Starting SSE for:', collectionId);
    setIsMonitoring(true);

    const cleanup = monitorCollectionMedia(
      collectionId,
      (event: MediaMonitorEvent) => {
        if (!isMountedRef.current) return;

        if (event.type === 'progress' && event.data) {
          dispatch(updateCollectionMedia(event.data));
        } else if (event.type === 'done' && event.data) {
          dispatch(updateCollectionMedia(event.data));
          setIsMonitoring(false);
          if (sseCleanupRef.current) {
            sseCleanupRef.current();
            sseCleanupRef.current = null;
          }
        } else if (event.type === 'error') {
          setIsMonitoring(false);
        }
      },
      () => {
        if (isMountedRef.current) setIsMonitoring(false);
      }
    );

    sseCleanupRef.current = cleanup;
  }, [collectionId, isMonitoring, dispatch]);

  // Auto-start monitoring when status is pending/processing
  useEffect(() => {
    if (!media || !collectionId || collectionId.startsWith('generated_')) return;

    const needsMonitoring =
      (media.image_status === 'pending' || media.image_status === 'processing' ||
       media.video_status === 'pending' || media.video_status === 'processing') &&
      !isMonitoring;

    if (needsMonitoring) startMediaMonitoring();
  }, [media, collectionId, isMonitoring, startMediaMonitoring]);

  // Cleanup on unmount or collection change
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (sseCleanupRef.current) {
        sseCleanupRef.current();
        sseCleanupRef.current = null;
      }
      setIsMonitoring(false);
    };
  }, [collectionId]);

  // ---------------------------------------------------------------------------
  // Self-healing: fetch media if not in Redux when viewer mounts/changes
  // Fixes timing issue where viewer renders before media fetch completes
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (collectionId && !collectionId.startsWith('generated_') && !media) {
      if (import.meta.env.DEV) console.log('[MediaViewer] No media data, fetching for:', collectionId);
      dispatch(fetchUserCollectionMediaRequest({ userId: APP_CONFIG.MEDIA_USER_ID }));
    }
  }, [collectionId, media, dispatch]);

  // ---------------------------------------------------------------------------
  // Regenerate handlers
  // ---------------------------------------------------------------------------
  const handleRegenImage = useCallback(() => {
    const urls = extractImageUrls(collection);
    if (urls.length === 0) return;
    dispatch(regenerateImageRequest({
      collectionId,
      userId: APP_CONFIG.MEDIA_USER_ID,
      imageUrls: urls,
    }));
  }, [collection, collectionId, dispatch]);

  const handleRegenVideo = useCallback(() => {
    dispatch(regenerateVideoRequest({
      collectionId,
      userId: APP_CONFIG.MEDIA_USER_ID,
    }));
  }, [collectionId, dispatch]);

  const canRegenImg = canRegenerateImage(imgStatus);
  const canRegenVid = imgStatus === 'completed' && canRegenerateVideo(vidStatus);

  // ---------------------------------------------------------------------------
  // Tab Button
  // ---------------------------------------------------------------------------
  const TabBtn = ({ tab, icon, label, status, canRegen, isRegen, onRegen }: {
    tab: TabType; icon: React.ReactNode; label: string;
    status?: MediaStatus; canRegen: boolean; isRegen: boolean; onRegen: () => void;
  }) => {
    const active = activeTab === tab;
    const badge = status ? statusConfig[status] : null;

    return (
      <button
        onClick={() => setActiveTab(tab)}
        className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all cursor-pointer ${
          active
            ? 'bg-[#1e1e1e] text-white border border-purple-500/50 shadow-sm'
            : 'bg-transparent text-gray-400 hover:text-gray-200 hover:bg-white/[0.03]'
        }`}
      >
        {icon}
        <span className="text-[13px] font-semibold">{label}</span>
        {badge && (
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${badge.cls}`}>
            {badge.text}
          </span>
        )}
        {canRegen && (
          <div
            onClick={(e) => { e.stopPropagation(); onRegen(); }}
            className={`ml-1 p-1.5 rounded-full transition-all shadow-md cursor-pointer ${
              isRegen ? 'bg-yellow-500 cursor-wait' : 'bg-purple-600 hover:bg-purple-700 hover:scale-110'
            }`}
            title={`Regenerate ${label.toLowerCase()}`}
            role="button"
            tabIndex={0}
          >
            <MdRefresh size={14} className={`text-white ${isRegen ? 'animate-spin' : ''}`} />
          </div>
        )}
      </button>
    );
  };

  // ---------------------------------------------------------------------------
  // Content Renderers
  // ---------------------------------------------------------------------------

  /** Professional generating state with animations */
  const renderGeneratingState = (type: 'image' | 'video') => {
    const mediaType = type === 'image' ? 'Image' : 'Video';
    return (
      <div className="flex flex-col items-center justify-center h-full py-8 px-6">
        {/* Animated icon */}
        <div className="relative mb-5">
          <div className="absolute inset-0 rounded-full blur-xl opacity-30 animate-pulse bg-purple-500" />
          <div className="relative w-18 h-18 rounded-full flex items-center justify-center bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30"
            style={{ width: 72, height: 72 }}
          >
            <MdAutoAwesome size={36} className="text-purple-400 animate-pulse" />
          </div>
        </div>

        {/* Title */}
        <h3 className="text-lg font-bold text-white mb-2">
          Generating Collection {mediaType}...
        </h3>

        {/* Status messages */}
        <div className="mb-4 text-center space-y-1.5 max-w-md">
          <p className="text-[13px] font-semibold text-purple-300 animate-pulse">
            🎨 AI is crafting your perfect collection {type}
          </p>
          <p className="text-[12px] text-gray-400 leading-relaxed">
            Analyzing products and composing professional {type}. Typically takes{' '}
            <span className="font-bold text-purple-300 animate-pulse">5-6 minutes</span>{' '}
            for exceptional quality.
          </p>
          <p className="text-[11px] text-gray-500 animate-pulse" style={{ animationDuration: '2s' }}>
            ⚡ Processing images, applying filters, optimizing...
          </p>
        </div>

        {/* Progress bar */}
        <div className="w-full max-w-xs">
          <div className="h-1 rounded-full bg-gray-700 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
              style={{
                width: '60%',
                animation: 'progress 2s ease-in-out infinite',
              }}
            />
          </div>
        </div>

        {/* Loading dots */}
        <div className="mt-4 flex gap-2 items-center">
          {[0, 150, 300].map((delay) => (
            <div
              key={delay}
              className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-bounce"
              style={{ animationDelay: `${delay}ms` }}
            />
          ))}
        </div>

        {/* SSE monitoring indicator */}
        {isMonitoring && (
          <div className="mt-4 flex items-center gap-2 text-[10px] text-gray-500">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            Live monitoring active
          </div>
        )}
      </div>
    );
  };

  const renderFailed = (type: string) => (
    <div className="flex flex-col items-center justify-center h-full py-16">
      <MdErrorOutline size={52} className="text-red-400 mb-4" />
      <p className="text-[14px] font-medium text-white mb-1">Generation Failed</p>
      <p className="text-[12px] text-gray-400">Collection {type} generation failed. Try regenerating.</p>
    </div>
  );

  const renderEmpty = () => (
    <div className="flex flex-col items-center justify-center h-full py-16">
      <div className="w-16 h-16 rounded-full bg-purple-500/10 flex items-center justify-center mb-4">
        <MdImage size={32} className="text-purple-400" />
      </div>
      <p className="text-[13px] text-gray-400 text-center max-w-xs">
        Your collection media will appear here automatically once generation is completed.
      </p>
    </div>
  );

  const renderImageContent = () => {
    if (!media) return renderEmpty();
    if (mediaError) return renderFailed('image');
    if (imgStatus === 'pending' || imgStatus === 'processing') return renderGeneratingState('image');
    if (imgStatus === 'failed' || !media.image_url) return renderFailed('image');
    return (
      <div className="relative w-full h-full">
        <img
          src={media.image_url}
          alt="Collection"
          className="w-full h-full object-contain"
          onError={(e) => {
            (e.target as HTMLImageElement).src =
              'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect width="400" height="400" fill="%231a1a1a"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="16" fill="%236b7280"%3EImage Load Error%3C/text%3E%3C/svg%3E';
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/5 to-transparent pointer-events-none" />
      </div>
    );
  };

  const renderVideoContent = () => {
    if (!media) return renderEmpty();
    if (vidStatus === 'pending' || vidStatus === 'processing') return renderGeneratingState('video');
    if (vidStatus === 'failed' || !media.video_url) return renderFailed('video');
    return (
      <video
        src={media.video_url}
        controls
        autoPlay
        loop
        className="w-full h-full object-contain bg-black"
      />
    );
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="w-full">
      {/* Tab Navigation */}
      <div className="flex items-center gap-2 p-1.5 rounded-t-xl bg-[#161616] border border-b-0 border-white/[0.06]">
        <TabBtn
          tab="image"
          icon={<MdImage size={16} />}
          label="Image"
          status={imgStatus}
          canRegen={canRegenImg}
          isRegen={isRegeneratingImg}
          onRegen={handleRegenImage}
        />
        <TabBtn
          tab="video"
          icon={<MdVideocam size={16} />}
          label="Video"
          status={vidStatus}
          canRegen={canRegenVid}
          isRegen={isRegeneratingVid}
          onRegen={handleRegenVideo}
        />
      </div>

      {/* Content Area */}
      <div
        className={`relative overflow-hidden rounded-b-xl border border-t-0 border-white/[0.06] bg-[#111] ${
          media?.image_url || media?.video_url ? 'h-[500px]' : 'h-[400px]'
        }`}
      >
        {activeTab === 'image' ? renderImageContent() : renderVideoContent()}
      </div>

      {/* CSS for progress animation */}
      <style>{`
        @keyframes progress {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(0%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
};

CollectionMediaViewer.displayName = 'CollectionMediaViewer';
export default CollectionMediaViewer;
